import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { AppDispatch, useAppSelector } from '../../../../redux/store';
import { ICategorieFinancial } from '../../../../redux/features/categorieFinancial/interface';
import { categoryFinancialSchema } from '../../../../redux/features/categorieFinancial/schema';
import {
  createCategoryFinancialAsync,
  updateCategoryFinancialAsync,
} from '../../../../redux/features/categorieFinancial/services';
import { notify, notifyError } from '../../../notify/notify-component';
import { ENTRY_TYPES, EntryType } from './entryTypes';
import * as S from './styles';

interface Props {
  show: boolean;
  type: EntryType;
  /** Categoria em edição; sem ela o modal cadastra uma nova. */
  category?: ICategorieFinancial;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  description: string;
  note: string;
  is_active: boolean;
}

export default function CategoriaModal({ show, type, category, onClose, onSaved }: Props) {
  const storeId = useAppSelector(({ storeMeReducer }) => storeMeReducer.value)?.storeActive?.id;
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: yupResolver(categoryFinancialSchema) as any });

  useEffect(() => {
    if (!show) return;
    reset({
      description: category?.description ?? '',
      note: category?.note ?? '',
      is_active: category?.is_active ?? true,
    });
  }, [show, category, reset]);

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    const payload = {
      description: values.description,
      note: values.note || null,
      is_active: values.is_active,
    };

    const result = category
      ? await dispatch(updateCategoryFinancialAsync({ uuid: category.uuid, ...payload }))
      : await dispatch(
          createCategoryFinancialAsync({
            ...payload,
            entry_type: ENTRY_TYPES[type].entryType,
            store_id: storeId,
          })
        );
    setSaving(false);

    if (result.meta.requestStatus === 'fulfilled') {
      notify(category ? 'Categoria atualizada.' : 'Categoria cadastrada.');
      onSaved();
      return;
    }
    notifyError((result.payload as any)?.message ?? 'Não foi possível salvar a categoria.');
  };

  return (
    <Modal show={show} onHide={() => !saving && onClose()} centered>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Modal.Header closeButton>
          <S.ModalTitle>
            {category ? 'Editar categoria' : 'Nova categoria'}
            <S.TypeBadge $tone={type}>{ENTRY_TYPES[type].label}</S.TypeBadge>
          </S.ModalTitle>
        </Modal.Header>
        <Modal.Body>
          <S.Fields>
            <S.Field $invalid={Boolean(errors.description)}>
              <span className="field-label">
                Descrição<em aria-hidden="true">*</em>
              </span>
              <input
                type="text"
                autoFocus
                maxLength={255}
                placeholder={type === 'revenue' ? 'Ex.: Vendas no balcão' : 'Ex.: Aluguel'}
                {...register('description')}
              />
              {errors.description && <small role="alert">{errors.description.message}</small>}
            </S.Field>

            <S.Field $invalid={Boolean(errors.note)}>
              <span className="field-label">Observação</span>
              <textarea maxLength={255} placeholder="Opcional" {...register('note')} />
              {errors.note && <small role="alert">{errors.note.message}</small>}
            </S.Field>

            <S.Switch className="form-check form-switch">
              <input
                id="category-active"
                type="checkbox"
                role="switch"
                className="form-check-input"
                {...register('is_active')}
              />
              <label htmlFor="category-active" className="m-0">
                Categoria ativa
              </label>
            </S.Switch>
          </S.Fields>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            {category ? 'Salvar alterações' : 'Cadastrar'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
